import { useEffect } from 'react';

import useAuth from '@/hooks/useAuth';

import { computeHasUnread } from '../lib/unread';
import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import { useAdminConversations } from './useAdminConversations';
import { useAdminReadState } from './useAdminReadState';

export function useCommsUnread(): { hasUnread: boolean } {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const { currentAdmin } = useAuth();
  const adminUserId = currentAdmin?.user?.id ?? null;
  const { conversations } = useAdminConversations();
  const { readMap, refresh } = useAdminReadState();

  // A read performed on the /chat-v2 page upserts admin_read_state through a
  // DIFFERENT SupabaseProvider instance, so this watcher's readMap won't see
  // it. Refresh on window focus (fallback) and, when available, on a Realtime
  // change to this admin's admin_read_state rows (primary, near-instant).
  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  // Same-window, infra-independent signal: a read on /chat-v2 (a separate
  // SupabaseProvider instance) emits this event after its upsert succeeds,
  // so this watcher refreshes instantly without depending on Realtime config.
  useEffect(() => {
    const onCommsRead = () => {
      void refresh();
    };
    window.addEventListener('comms:read', onCommsRead);
    return () => window.removeEventListener('comms:read', onCommsRead);
  }, [refresh]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refresh]);

  useEffect(() => {
    if (!client || !ready || !adminUserId) return;
    const channel = client
      .channel(`admin-read-state-watch:${adminUserId}:${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_read_state',
          filter: `admin_user_id=eq.${Number(adminUserId)}`,
        },
        () => {
          void refresh();
        },
      );
    channel.subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, ready, adminUserId, refresh]);

  return { hasUnread: computeHasUnread(conversations, readMap) };
}
